import { Order, OrderList, OrderMessages, type OrderStatus } from '@deizi/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { jobsKey } from './queue-api';

export const ordersKey = ['orders'] as const;

export interface OrderFilters {
  statuses: OrderStatus[];
  channelId: string;
  late: boolean;
  q: string;
  sort: 'ship_by' | 'recent';
}

export function orderParams(f: OrderFilters): URLSearchParams {
  const params = new URLSearchParams({ limit: '200', sort: f.sort });
  if (f.statuses.length) params.set('status', f.statuses.join(','));
  if (f.channelId) params.set('channelId', f.channelId);
  if (f.late) params.set('late', 'true');
  if (f.q.trim()) params.set('q', f.q.trim());
  return params;
}

export function useOrders(f: OrderFilters) {
  const params = orderParams(f);
  return useQuery({
    queryKey: [...ordersKey, 'list', params.toString()],
    queryFn: () => api.get(`/orders?${params.toString()}`, OrderList),
    placeholderData: (prev) => prev,
  });
}

export function useOrder(id: string | undefined) {
  return useQuery({
    queryKey: [...ordersKey, id],
    queryFn: () => api.get(`/orders/${id}`, Order),
    enabled: !!id,
  });
}

export function useOrderMessages(id: string) {
  return useQuery({
    queryKey: [...ordersKey, id, 'messages'],
    queryFn: () => api.get(`/orders/${id}/messages`, OrderMessages),
  });
}

/** Orders move jobs, stock, supplies and quotes too. */
export function useInvalidateOrders() {
  const qc = useQueryClient();
  return () => {
    for (const key of [
      ordersKey,
      jobsKey,
      ['quotes'],
      ['products'],
      ['supplies'],
      ['spools'],
      ['inventory'],
    ]) {
      void qc.invalidateQueries({ queryKey: key });
    }
  };
}

/** "08/10" from "2026-10-08". */
export const dayMonth = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
